class ProductInfoTabsElement extends HTMLElement {
  connectedCallback() {
    this._controller = new ProductInfoTabs(this);
  }

  disconnectedCallback() {
    this._controller?.destroy();
  }
}

class ProductInfoTabs {
  constructor(element) {
    this.element = element;
    this.abortController = new AbortController();
    this.setup();
  }

  destroy() {
    this.abortController.abort();
  }

  setup() {
    if (this.element.dataset.productInfoTabsInitialized === 'true') {
      return;
    }

    this.element.dataset.productInfoTabsInitialized = 'true';
    this.panels = Array.from(this.element.querySelectorAll('[data-tab-content]'));
    this.items = this.panels.map((panel) => panel.closest('.product-info__block-item') || panel);

    if (this.panels.length < 2) {
      this.element.setAttribute('data-loaded', 'true');
      return;
    }

    this.buildTabs();

    this.buttons.forEach((button, index) => {
      button.addEventListener('click', () => this.select(index), { signal: this.abortController.signal });
      button.addEventListener('keydown', (event) => this.handleKeydown(event, index), { signal: this.abortController.signal });
    });

    if (Shopify.designMode) {
      this.element.addEventListener('shopify:block:select', (event) => {
        const panel = event.target.querySelector('[data-tab-content]');
        const index = this.panels.indexOf(panel);

        if (index >= 0) {
          this.select(index);
        }
      }, { signal: this.abortController.signal });
    }

    this.select(0, false);
    this.element.setAttribute('data-loaded', 'true');
  }

  buildTabs() {
    const nav = document.createElement('div');
    const content = document.createElement('div');
    const componentId = this.element.id || `product-info-tabs-${Math.floor(Math.random() * 1e6)}`;

    if (!this.element.id) {
      this.element.id = componentId;
    }

    nav.className = 'product-info-tabs__nav';
    nav.setAttribute('role', 'tablist');

    content.className = 'product-info-tabs__content';

    this.buttons = this.panels.map((panel, index) => {
      const item = this.items[index];
      const button = document.createElement('button');
      const title = panel.getAttribute('data-tab-title') || `Tab ${index + 1}`;
      const panelId = `${componentId}-panel-${index}`;
      const buttonId = `${componentId}-tab-${index}`;

      button.type = 'button';
      button.className = 'product-info-tabs__tab';
      button.textContent = title;
      button.dataset.tabSwitch = '';
      button.dataset.index = index.toString();
      button.id = buttonId;
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-controls', panelId);

      if (index === 0) {
        button.dataset.first = '';
      } else if (index === this.panels.length - 1) {
        button.dataset.last = '';
      } else {
        button.dataset.next = '';
      }

      panel.dataset.index = index.toString();
      panel.id = panelId;
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', buttonId);

      item.dataset.tabItem = '';
      item.dataset.index = index.toString();

      nav.append(button);
      return button;
    });

    this.element.prepend(nav);
    nav.after(content);

    this.items.forEach((item) => {
      content.append(item);
    });
  }

  select(index, focus = true) {
    this.buttons.forEach((button, buttonIndex) => {
      const selected = buttonIndex === index;

      button.dataset.active = selected ? 'true' : 'false';
      button.setAttribute('aria-selected', selected ? 'true' : 'false');
      button.tabIndex = selected ? 0 : -1;
    });

    this.panels.forEach((panel, panelIndex) => {
      const selected = panelIndex === index;
      const item = this.items[panelIndex];

      panel.dataset.active = selected ? 'true' : 'false';
      panel.hidden = !selected;
      item.dataset.active = selected ? 'true' : 'false';
      item.hidden = !selected;
    });

    if (focus) {
      this.buttons[index]?.focus({ preventScroll: true });
    }
  }

  handleKeydown(event, index) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
      return;
    }

    event.preventDefault();

    if (event.key === 'Home') {
      this.select(0);
    } else if (event.key === 'End') {
      this.select(this.buttons.length - 1);
    } else if (event.key === 'ArrowLeft') {
      this.select((index - 1 + this.buttons.length) % this.buttons.length);
    } else {
      this.select((index + 1) % this.buttons.length);
    }
  }
}

function initProductInfoTabs(root = document) {
  root.querySelectorAll('[data-group-type="product-info-tabs"]').forEach((element) => new ProductInfoTabs(element));
}

if (!window.customElements.get('product-info-tabs')) {
  window.customElements.define('product-info-tabs', ProductInfoTabsElement);
}

initProductInfoTabs();

document.addEventListener('shopify:section:load', (event) => initProductInfoTabs(event.target));
